import { deleteRegistration } from '@commenda/api';
import { useCallback, useRef } from 'react';
import { useNotification } from '../../../../../hooks/useNotification';
import { DeleteConfirmationModal } from '../../../../../molecules/modals/ConfirmationModal';

interface DeleteRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  registrationName: string;
  registrationId: string;
  onSuccess: () => void;
  businessEntityId: number;
}

export const DeleteRegistrationModal = ({
  isOpen,
  onClose,
  registrationName,
  registrationId,
  onSuccess,
  businessEntityId,
}: DeleteRegistrationModalProps) => {
  const { setNotification } = useNotification();

  // Callers derive the name from the row being acted on and release that row inside onSuccess,
  // which runs while the dialog is still open and animating out. Hold the last name we were
  // given so the title can't fall back to "Delete undefined" on the way out.
  const shownName = useRef(registrationName);
  if (registrationName) shownName.current = registrationName;

  const handleDeleteRegistration = useCallback(async () => {
    try {
      await deleteRegistration(businessEntityId, registrationId);
      await onSuccess();
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: `${shownName.current} has been successfully deleted.`,
      });
    } catch (error) {
      console.error('Failed to delete registration:', error);
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: `Failed to delete ${shownName.current}. Please try again.`,
      });
      throw error;
    }
  }, [businessEntityId, registrationId, onSuccess, setNotification]);

  return (
    <DeleteConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleDeleteRegistration}
      title={`Delete ${shownName.current}`}
      message="This action cannot be undone. Are you sure you want to delete this registration?"
    />
  );
};
